import React from "react";
import PropTypes from "prop-types";
import { Breadcrumbs, Card, Picture } from "components/UI";
import styled from "@emotion/styled";
import { imgAboutPriya, imgAboutNura } from "assets";
import classnames from "classnames";
import FeaturedLayout from "./common/FeaturedLayout";
import Apply from "./common/Apply";
import {
  imgAboutEd,
  imgAboutAhikaa,
  imgAboutNiam,
  imgAboutMeher,
  imgAboutMazuin,
  imgAboutJoannaWong,
} from "../../../public/assets";
import { generalEmails } from "../../utils/constants";

const TEAM_BG_HEIGHT = 1500;

const TeamBackground = styled.div`
  height: ${TEAM_BG_HEIGHT}px;
  clip-path: polygon(0 6%, 100% 0%, 100% 94%, 0% 100%);
`;

const TeamSectionContainer = styled.section`
  margin-top: -${TEAM_BG_HEIGHT + 75}px;
`;

const TeamCardContainer = styled(Card)`
  max-width: 800px;
`;

const TeamCardPicture = styled(Picture)`
  max-height: 360px;
`;

const TeamCard = ({ image, name, position, country, about, reverse }) => (
  <div
    className={classnames("flex flex-col-reverse items-center mt-16", {
      "md:flex-row-reverse": reverse,
      "md:flex-row": !reverse,
    })}
  >
    <TeamCardContainer
      className={classnames("border-easter py-12 px-8 -mt-8 md:mt-0", {
        "border-r-6 md:pr-12 md:pl-32": reverse,
        "border-l-6 md:pr-32 md:pl-12": !reverse,
      })}
    >
      <h5 className="text-2lg text-easter">{name}</h5>
      <strong className="block text-md font-semibold">{position}</strong>
      <strong className="block mb-4 font-normal text-boulder">{country}</strong>
      <p>{about}</p>
    </TeamCardContainer>
    <TeamCardPicture
      src={image}
      className={classnames("max-w-xs relative rounded", {
        "md:-mr-12": reverse,
        "md:-ml-12": !reverse,
      })}
    />
  </div>
);

TeamCard.propTypes = {
  image: PropTypes.object.isRequired,
  name: PropTypes.string.isRequired,
  position: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  about: PropTypes.string.isRequired | HTMLElement,
  reverse: PropTypes.bool,
};

TeamCard.defaultProps = {
  reverse: false,
};

const TeamSection = () => (
  <TeamSectionContainer className="mb-20 relative">
    <div className="flex flex-col items-center container mx-auto px-6">
      <TeamCard
        image={imgAboutPriya}
        name="Priya Dewan"
        country="Singapore"
        position="Founder and CEO"
        about="Priya Dewan launched Gig Life Pro to empower today’s music industry creatives and professionals with
        the information and networking opportunities that helped her build a 20+ year globally successful career
        in the music industry. Dewan’s first major success was at the start of her career running Warp Records
        N. America in New York where she helped Grizzly Bear’s Vecketamist debut at #8 on Billboard’s Top 200
        and received the honor of “30 Under 30 Power Players”. She then returned to Singapore to launch
        Feedback Asia, the first direct to management booking Asia for Asia, based in Asia, representing artists
        like CHVRCHES, Flying Lotus, Simian Mobile Disco, Bicep and Beach House. Most recently Dewan was
        VP of International Marketing APAC and MD of Southeast Asia and South Korea for The Orchard, where
        she received Billboard’s “International Power Player” twice for her role in signing and overseeing global
        campaigns for artists like BTS, TWICE, Stray Kids, ITZY, DPR, Eric Nam, CL, G.E.M., Son Tung MTP-E,
        James Reid, Shigga Shay. Priya has facilitated or spoken on conference panels at All That Matters
        (Singapore), SXSW Sydney, MU:CON (Seoul), EMC (Syd), GMAs (Taipei), Sound of the Xity (Beijing),
        Primavera Pro (Barcelona), CMJ (New York), SXSW (Austin)."
        reverse
      />
      <TeamCard
        image={imgAboutEd}
        name="Edward Tan"
        country="Singapore"
        position="VP of Growth"
        about="With over a decade of experience in integrated media, content, and digital marketing, Edward Tan
            specializes in orchestrating comprehensive 360-degree marketing campaigns for renowned brands such
            as CapitaLand, MM2 Entertainment, Cathay Group, MediaCorp, Esplanade, and TVB Hong Kong. In his
            multifaceted role, Ed leverages his in-depth expertise across SEO, PPC, social media, email marketing,
            and analytics to consistently deliver exceptional results.
            
            As a strategic and creative thinker, he notably led the Subway Singapore team to secure the Best Use of
      Mobile Advertising at the Singapore Media Award in 2019. Beyond the professional realm, Ed finds
    enjoyment in watching Disney movies and grooving to his eclectic playlist, featuring a fusion of English,
    Chinese, Thai, Vietnamese, and Italian music. Currently serving as the VP of Growth at Gig Life Pro, Ed continues to bring his wealth of experience to drive success in the marketing arena.
            "
      />
      {/* <TeamCard
        image={imgAboutNiam}
        name="Niam Hegarty"
        country="Australia"
        position="VP of Content"
        about="Operating out of Melbourne, Australia, Niam comes to Gig Life Pro from a Music Publicity and Digital Marketing background. Niam has worked for Diamond Creative Services as a Publicist working on releases from labels such as Epitaph/Anti, Hopeless Records and Australian tours for emerging and international artists. Niam also runs his own boutique PR, digital marketing, and artist management business Lost Souls where he works with emerging artists to develop their careers. Niam is an experienced touring musician, currently serving as lead vocalist, songwriter and guitarist for Melbourne based indie outfit Dental Plan."
        reverse
      /> */}
      <TeamCard
        image={imgAboutJoannaWong}
        name="Joanna Wong"
        country="Singapore"
        position="Finance & HR Manager"
        about={
          <>
            Joanna Wong is a finance and operations professional with over 20 years of
            experience across accounting, business management, operations, and process improvement.
            Her background spans multinational and SME environments, including roles in
            finance analysis, operations leadership, business development, payroll, compliance, budgeting, forecasting, and management reporting. <br />
            <br /> She has supported organisations through financial controls, cashflow management,
            workflow improvement, ERP/accounting system processes, and cross-functional coordination.
            With a Six Sigma Green Belt and recent training in data analytics, Joanna also brings a strong interest in
            using automation, Power BI, Excel VBA, AI tools, and data-driven reporting to improve business efficiency and decision-making. <br />
            <br /> At Gig Life Pro, Joanna supports the Finance and HR functions, including accounting processes, payroll-related matters,
            reporting, compliance follow-up, and operational workflow improvements. She enjoys bringing structure to fast-growing environments and
            helping teams work with clearer processes, better visibility, and stronger financial discipline.
          </>
        }
      />
      <TeamCard
        image={imgAboutNura}
        name="Nurazillah Salleh"
        country="Singapore"
        position="Product & Data Analyst"
        about="Nurazillah Salleh has worn many hats—customer support, business analysis, product analytics—at companies like Accenture @ Meta, Grab, and Wise, all while focusing on simplifying complex problems. With diplomas in data science and psychology, she’s passionate about uncovering insights and translating them into actionable solutions. Her early experience in secondary school's military band, where she honed the fine art of teamwork and rhythm, laid the foundation for her collaborative approach to problem-solving and enduring love of music. Outside of work, you’ll find Nura devouring period novels, exploring museums, volunteering with Fitrah.sg, traveling, or hunting for the next best meal (because she appreciates the importance of a good meal). If she’s not analyzing trends, she’s probably planning her next adventure or trying to figure out what new book to start (because there’s never enough time for all the books she wants to read)."
        reverse
      />
      <TeamCard
        image={imgAboutAhikaa}
        name="Ahikaa Arora"
        country="Singapore"
        position="Community Manager"
        about="Ahikaa, a Berklee College of Music scholarship graduate, has been driven by a lifelong passion for music. Holding a degree in Music Business, she has been building a diverse career, including A&R experiences at el3ment, Position Music, and APG in Los Angeles. Her professional journey also encompasses contributions to Berklee's A&R group and an independent label, where she scouted artists, organized pitching sessions with major labels such as RCA Records and Atlantic Records, and facilitated a notable event featuring Sophia Eris (Lizzo's DJ) to enhance Berklee artist visibility. As a singer-songwriter, Ahikaa takes pleasure in self-expression, with her debut single receiving airplay on Kiss 92FM in Singapore. Her commitment to the music community extends beyond her personal career; she has volunteered at the AXEAN Festival in Singapore, taught voice lessons to nurture aspiring singers, and is now channeling her passion into Gig Life Pro. At Gig Life Pro, Ahikaa is dedicated to serving the community that has been integral to her journey, connecting with industry peers, and actively shaping her path through hands-on involvement. Guided by her belief in following one’s intuition, being proactive, and fostering collaboration, she remains deeply engaged in this business. Her journey reflects a desire to reach her highest potential, striving to grow through every opportunity."
      />
      <TeamCard
        image={imgAboutMeher}
        name="Meher Motial"
        country="Singapore"
        position="Events Manager"
        about={
          <>
            Meher thrives at Gig Life Pro, and as GLP’s Events Manager, she aims to curate live and
            online experiences where you can thrive in this community too. Born into a musical
            family, her passion for music began at a young age and led her to pursue a degree in
            Music Business and Songwriting at Berklee College of Music, where she was awarded a
            scholarship. She’s since honed her skills in live event production, artist relations,
            and marketing through roles at Universal Music Group’s Revere Music Group,{" "}
            <a className="text-easter underline" href="https://programming.com/">
              Programming.com
            </a>
            , and Singapore-based events company Dream Catchers Vision, where she oversaw end-to-end
            event management—from vendor coordination and event marketing to show curation, on-site
            facilitation, and post-event relationship building to ensure lasting connections and
            community engagement. Outside of work, Meher loves creating and listening to island
            music (Naïka and June Freedom are her favorite artists), cycling by the beach with
            friends, and swimming with sharks (she did it once but has since made it her entire
            personality). At GLP, she continues to bring her skills and passion to the
            community—curating mixers, panels, showcases, online connects, and custom events that
            make people, places, and possibilities fit just right.
          </>
        }
        reverse
      />

      {/* <TeamCard
        image={imgAboutMazuin}
        name="Mazuin Zien"
        country="Singapore"
        position="Executive Assistant"
        about="Mazuin Zien is a versatile professional with over a decade of experience in photography, digital design, and executive support across corporate, creative, and beauty industries. Skilled in client management, project coordination, and content creation, she thrives in fast-paced environments and brings both precision and creativity to her work. Alongside her creative expertise, Mazuin is expanding into Data Science through a Diploma in Infocomm Technology, building skills in Python, machine learning, data engineering, and data visualization. This blend of creative and analytical strengths enables her to approach challenges with technical rigor and artistic perspective. At Gig Life Pro, Mazuin supports the CEO and wider team by building seamless systems and ensuring smooth operations for the gig community. Outside of work, she continues to pursue photography and design projects, drawing inspiration from diverse industries and everyday life."
      /> */}
      <h2 className="font-semibold mt-12 text-center">General Enquiries</h2>
      <Card className="w-full md:w-4/6 relative mt-16 border-easter border-t-6 overflow-x-auto">
        <table className="w-full text-sm text-left rtl:text-right">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3">
                DEPARTMENT
              </th>
              <th scope="col" className="px-6 py-3">
                EMAIL
              </th>
            </tr>
          </thead>
          <tbody>
            {generalEmails?.map(({ department, email }) => (
              <tr key={department} className="bg-white border-b ">
                <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">
                  {department}
                </th>
                <td className="px-6 py-4 text-gray-900 break-all">{email}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  </TeamSectionContainer>
);

const Header = () => (
  <FeaturedLayout bgFilter={false} polygon={false}>
    <div className="container sm:px-4 mx-auto z-0">
      <Breadcrumbs trails={[{ label: "About Us", path: "/about" }]} publicRoute />
    </div>
    <div className="flex flex-col text-center text-white relative m-auto">
      <h1 className="text-3xl font-semibold">About Us</h1>
      <p className="text-lg opacity-0">
        A little background about our services and the founders of the community
      </p>
    </div>
  </FeaturedLayout>
);

const About = () => (
  <div>
    <Header />
    <TeamBackground className="bg-lily mt-20" />
    <TeamSection />
    <Apply />
  </div>
);

About.propTypes = {};

export default About;
